import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Building2, ChevronDown, Menu, X } from "lucide-react";
import { UserMenu } from './UserMenu';
import { useAuth } from '../hooks/useAuth';
import { useComparison } from '../hooks/useComparison';
import { toast } from 'sonner';
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Navbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { compareIds } = useComparison();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handlePublicarClick = () => {
    if (!user) {
      toast.info("Necesitas iniciar sesión para publicar una propiedad", {
        action: {
          label: "Registrarse",
          onClick: () => navigate('/signup')
        }
      });
      navigate('/login');
      return;
    }
    navigate('/publicar');
  };

  const navLinks = [
    { name: "Propiedades", path: "/propiedades" },
    { name: "Planes", path: "/planes" },
    { name: "Nosotros", path: "/nosotros" },
  ];

  const services = [
    { name: "CRM Inmobiliario", path: "/servicios/crm" },
    { name: "Ecosistema Digital IA", path: "/servicios/ecosistema-digital" },
    { name: "Valorización IA", path: "/servicios/valorizacion" },
    { name: "Vende tu Propiedad", path: "/servicios/vender-directo" },
    { name: "Legal & Contable", path: "/servicios/legal-contable" },
  ];

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b-2 border-ink",
      isScrolled ? "bg-parchment py-2" : "bg-parchment/95 backdrop-blur-sm py-4"
    )}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-[64px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="bg-ink p-1.5 transition-transform group-hover:-rotate-3">
              <Building2 className="h-6 w-6 text-parchment" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-display font-extrabold uppercase text-ink">Propiedades</span>
              <span className="text-xs font-sans tracking-[0.2em] uppercase text-terracotta font-bold">LATAM Editorial</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-ink/60 hover:text-terracotta transition-colors outline-none">
                Propiedades <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-parchment border-2 border-ink rounded-none p-2 min-w-[220px] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <DropdownMenuItem className="focus:bg-ink focus:text-parchment rounded-none cursor-pointer" asChild>
                  <Link to="/propiedades" className="w-full text-[10px] font-black uppercase tracking-widest p-3">
                    Catálogo de Activos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="focus:bg-ink focus:text-parchment rounded-none cursor-pointer" asChild>
                  <Link to="/comparar" className="w-full text-[10px] font-black uppercase tracking-widest p-3 flex justify-between items-center">
                    Comparador de Mercado
                    {compareIds.length > 0 && (
                      <span className="bg-terracotta text-white px-1.5 py-0.5 text-[8px] font-bold">
                        {compareIds.length}/3
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-ink/60 hover:text-terracotta transition-colors outline-none">
                Servicios <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-parchment border-2 border-ink rounded-none p-2 min-w-[200px] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                {services.map((service) => (
                  <DropdownMenuItem 
                    key={service.path}
                    className="focus:bg-ink focus:text-parchment rounded-none cursor-pointer"
                    asChild
                  >
                    <Link to={service.path} className="w-full text-[10px] font-black uppercase tracking-widest p-3">
                      {service.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/planes" 
              className="text-[10px] font-black uppercase tracking-widest text-ink/60 hover:text-terracotta transition-colors"
            >
              Planes
            </Link>
            
            <Link 
              to="/nosotros" 
              className="text-[10px] font-black uppercase tracking-widest text-ink/60 hover:text-terracotta transition-colors"
            >
              Nosotros
            </Link>
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-6">
            <button
              onClick={handlePublicarClick}
              className="hidden md:block text-xs font-bold uppercase tracking-widest text-ink hover:text-terracotta transition-colors underline underline-offset-4 decoration-terracotta/30"
            >
              Publicar
            </button>

            {user ? (
              <UserMenu 
                user={{
                  name: user.name || 'Usuario',
                  email: user.email,
                  avatar: user.avatar
                }}
                onLogout={logout}
              />
            ) : (
              <Button
                variant="default"
                onClick={() => navigate('/login')}
                className="brutalist-border-sm bg-terracotta hover:bg-ink text-parchment font-bold uppercase tracking-widest text-xs px-6"
              >
                Ingresar
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-parchment border-t-2 border-ink p-8 animate-in slide-in-from-top duration-300">
          <nav className="flex flex-col gap-6">
            <div className="space-y-4">
              <span className="text-[10px] font-black uppercase tracking-widest text-terracotta">Propiedades</span>
              <Link to="/propiedades" className="block text-lg font-display font-black uppercase pl-4" onClick={() => setIsMobileMenuOpen(false)}>Catálogo</Link>
              <Link to="/comparar" className="block text-lg font-display font-black uppercase pl-4 flex justify-between items-center" onClick={() => setIsMobileMenuOpen(false)}>
                Comparador
                {compareIds.length > 0 && <span className="bg-terracotta text-white px-2 py-0.5 text-xs">{compareIds.length}/3</span>}
              </Link>
            </div>
            
            <div className="space-y-4">
              <span className="text-[10px] font-black uppercase tracking-widest text-terracotta">Servicios</span>
              {services.map((service) => (
                <Link 
                  key={service.path} 
                  to={service.path} 
                  className="block text-lg font-display font-black uppercase pl-4"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {service.name}
                </Link>
              ))}
            </div>
            <Link to="/planes" className="text-xl font-display font-black uppercase italic" onClick={() => setIsMobileMenuOpen(false)}>Planes</Link>
            <Link to="/nosotros" className="text-xl font-display font-black uppercase italic" onClick={() => setIsMobileMenuOpen(false)}>Nosotros</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
